import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import { api } from './api';

// Values arrive metric (°C, km/h, mm, snowfall in cm) and are converted for
// display here. Hourly and current times are unix seconds.

export type TemperatureUnit = 'celsius' | 'fahrenheit';
export type WindSpeedUnit = 'kmh' | 'ms' | 'mph' | 'kn';
export type PrecipitationUnit = 'mm' | 'inch';
export type AqiScale = 'us' | 'european';

export type CellStat =
  | 'high_low'
  | 'feels_like'
  | 'precip_prob'
  | 'precip_sum'
  | 'snow_sum'
  | 'wind_gusts'
  | 'uv_max'
  | 'aqi_max';
export type DetailStat =
  | 'feels_like'
  | 'humidity'
  | 'wind'
  | 'gusts'
  | 'precip'
  | 'uv'
  | 'aqi'
  | 'pm2_5'
  | 'sun'
  | 'daylight';
export type Overlay =
  | 'header_chip'
  | 'month'
  | 'month_temp'
  | 'month_precip'
  | 'month_sky'
  | 'week'
  | 'week_graph'
  | 'day';

export interface WeatherLocation {
  id: string;
  name: string;
  region: string | null;
  latitude: number;
  longitude: number;
  timezone: string;
  fetched_at: string | null;
  fetch_error: string | null;
}

export interface WeatherPrefs {
  active_location_id: string | null;
  model: string;
  temperature_unit: TemperatureUnit;
  wind_speed_unit: WindSpeedUnit;
  precipitation_unit: PrecipitationUnit;
  aqi_scale: AqiScale;
  cell_stats: CellStat[];
  detail_stats: DetailStat[];
  overlays: Overlay[];
  locations: WeatherLocation[];
}

export interface DayData {
  weather_code: number | null;
  temperature_2m_max: number | null;
  temperature_2m_min: number | null;
  apparent_temperature_max: number | null;
  apparent_temperature_min: number | null;
  precipitation_sum: number | null;
  snowfall_sum: number | null;
  precipitation_probability_max: number | null;
  wind_speed_10m_max: number | null;
  wind_gusts_10m_max: number | null;
  wind_direction_10m_dominant: number | null;
  uv_index_max: number | null;
  sunrise: number | null;
  sunset: number | null;
  daylight_duration: number | null;
  us_aqi_max?: number | null;
  european_aqi_max?: number | null;
  // The model the day came from: the chosen one, or the fallback where the
  // chosen model has no data (history gaps, days past its forecast range).
  model: string;
}

// A future day forecast by the fallback model because the chosen one doesn't
// reach it.
export function isExtended(date: string, day: DayData, prefs: WeatherPrefs): boolean {
  return day.model !== prefs.model && date >= format(new Date(), 'yyyy-MM-dd');
}

export interface HourData {
  t: number;
  temperature_2m: number | null;
  apparent_temperature: number | null;
  precipitation_probability: number | null;
  precipitation: number | null;
  snowfall?: number | null; // cm
  cloud_cover?: number | null; // %
  weather_code: number | null;
  is_day: number | null;
  wind_speed_10m: number | null;
  wind_gusts_10m: number | null;
  uv_index: number | null;
  us_aqi?: number | null;
  european_aqi?: number | null;
  pm2_5?: number | null;
}

export interface CurrentData {
  time: number;
  temperature_2m: number | null;
  apparent_temperature: number | null;
  relative_humidity_2m: number | null;
  weather_code: number | null;
  is_day: number | null;
  precipitation: number | null;
  wind_speed_10m: number | null;
  wind_direction_10m: number | null;
  wind_gusts_10m: number | null;
  uv_index: number | null;
}

export interface AirData {
  us_aqi: number | null;
  european_aqi: number | null;
  pm2_5: number | null;
  pm10: number | null;
  ozone: number | null;
}

export interface WeatherResponse {
  location: WeatherLocation | null;
  model: string | null;
  current: CurrentData | null;
  air: AirData | null;
  hourly: HourData[];
  days: Record<string, DayData>;
}

// Everything a calendar view needs to draw weather, bundled so the views take
// one prop.
export interface WeatherView {
  prefs: WeatherPrefs;
  data: WeatherResponse;
  hoursByDay: Map<string, HourData[]>;
}

export const MODELS: { id: string; name: string; detail: string }[] = [
  { id: 'best_match', name: 'Best match', detail: 'Open-Meteo picks the models for the location. 16 days.' },
  { id: 'gem_seamless', name: 'GEM', detail: 'Environment Canada, 2.5 km for the first two days. 9 days.' },
  { id: 'ecmwf_ifs', name: 'ECMWF IFS 9 km', detail: 'European Centre high-resolution model. 14 days.' },
  { id: 'ecmwf_ifs025', name: 'ECMWF IFS 0.25°', detail: 'The open-data ECMWF run at 25 km. 14 days.' },
  { id: 'ecmwf_aifs025_single', name: 'ECMWF AIFS', detail: 'ECMWF machine-learning model. 15 days, no chance of precipitation.' },
  { id: 'ncep_gfs_seamless', name: 'GFS', detail: 'NOAA global model. 16 days.' },
  { id: 'dwd_icon_seamless', name: 'ICON', detail: 'German Weather Service. 7 days.' },
  { id: 'ukmo_seamless', name: 'UK Met Office', detail: '6 days, no chance of precipitation.' },
  { id: 'jma_seamless', name: 'JMA', detail: 'Japan Meteorological Agency. 11 days, no chance of precipitation.' }
];

export function modelName(id: string | null | undefined): string {
  return MODELS.find((m) => m.id === id)?.name ?? id ?? '';
}

export const CELL_STATS: { id: CellStat; label: string }[] = [
  { id: 'high_low', label: 'High and low' },
  { id: 'feels_like', label: 'Feels-like high' },
  { id: 'precip_prob', label: 'Chance of precipitation' },
  { id: 'precip_sum', label: 'Precipitation amount' },
  { id: 'snow_sum', label: 'Snowfall' },
  { id: 'wind_gusts', label: 'Strongest gust' },
  { id: 'uv_max', label: 'UV index peak' },
  { id: 'aqi_max', label: 'Air quality peak' }
];

export const DETAIL_STATS: { id: DetailStat; label: string }[] = [
  { id: 'feels_like', label: 'Feels like' },
  { id: 'humidity', label: 'Humidity' },
  { id: 'wind', label: 'Wind' },
  { id: 'gusts', label: 'Gusts' },
  { id: 'precip', label: 'Precipitation' },
  { id: 'uv', label: 'UV index' },
  { id: 'aqi', label: 'Air quality' },
  { id: 'pm2_5', label: 'PM2.5' },
  { id: 'sun', label: 'Sunrise and sunset' },
  { id: 'daylight', label: 'Daylight' }
];

export const OVERLAYS: { id: Overlay; label: string }[] = [
  { id: 'header_chip', label: 'Current conditions in the top bar' },
  { id: 'month', label: 'Month view day cells' },
  { id: 'month_temp', label: 'Month view temperature graph' },
  { id: 'month_precip', label: 'Month view precipitation bars' },
  { id: 'month_sky', label: 'Month view sky ribbon (daylight and cloud)' },
  { id: 'week', label: 'Week view day headers' },
  { id: 'week_graph', label: 'Week view weather graph' },
  { id: 'day', label: 'Day view conditions and hourly forecast' }
];

// --- WMO weather codes ------------------------------------------------------

export type IconKind =
  | 'clear'
  | 'partly'
  | 'cloudy'
  | 'fog'
  | 'drizzle'
  | 'rain'
  | 'freezing'
  | 'snow'
  | 'showers'
  | 'snow_showers'
  | 'thunder';

const CODES: Record<number, { label: string; icon: IconKind }> = {
  0: { label: 'Clear', icon: 'clear' },
  1: { label: 'Mainly clear', icon: 'clear' },
  2: { label: 'Partly cloudy', icon: 'partly' },
  3: { label: 'Overcast', icon: 'cloudy' },
  45: { label: 'Fog', icon: 'fog' },
  48: { label: 'Freezing fog', icon: 'fog' },
  51: { label: 'Light drizzle', icon: 'drizzle' },
  53: { label: 'Drizzle', icon: 'drizzle' },
  55: { label: 'Heavy drizzle', icon: 'drizzle' },
  56: { label: 'Light freezing drizzle', icon: 'freezing' },
  57: { label: 'Freezing drizzle', icon: 'freezing' },
  61: { label: 'Light rain', icon: 'rain' },
  63: { label: 'Rain', icon: 'rain' },
  65: { label: 'Heavy rain', icon: 'rain' },
  66: { label: 'Light freezing rain', icon: 'freezing' },
  67: { label: 'Freezing rain', icon: 'freezing' },
  71: { label: 'Light snow', icon: 'snow' },
  73: { label: 'Snow', icon: 'snow' },
  75: { label: 'Heavy snow', icon: 'snow' },
  77: { label: 'Snow grains', icon: 'snow' },
  80: { label: 'Light showers', icon: 'showers' },
  81: { label: 'Showers', icon: 'showers' },
  82: { label: 'Violent showers', icon: 'showers' },
  85: { label: 'Snow showers', icon: 'snow_showers' },
  86: { label: 'Heavy snow showers', icon: 'snow_showers' },
  95: { label: 'Thunderstorm', icon: 'thunder' },
  96: { label: 'Thunderstorm with hail', icon: 'thunder' },
  99: { label: 'Thunderstorm with heavy hail', icon: 'thunder' }
};

export function condition(code: number | null | undefined): { label: string; icon: IconKind } | null {
  if (code == null) return null;
  return CODES[code] ?? null;
}

// --- Units and formatting ---------------------------------------------------

export function convertTemp(c: number, unit: TemperatureUnit): number {
  return unit === 'fahrenheit' ? c * 1.8 + 32 : c;
}

export function fmtTemp(c: number | null | undefined, unit: TemperatureUnit): string {
  if (c == null) return '–';
  return `${Math.round(convertTemp(c, unit))}°`;
}

const WIND_FACTOR: Record<WindSpeedUnit, number> = { kmh: 1, ms: 1 / 3.6, mph: 1 / 1.609344, kn: 1 / 1.852 };
export const WIND_LABEL: Record<WindSpeedUnit, string> = { kmh: 'km/h', ms: 'm/s', mph: 'mph', kn: 'kn' };

export function convertWind(kmh: number, unit: WindSpeedUnit): number {
  return kmh * WIND_FACTOR[unit];
}

export function fmtWind(kmh: number | null | undefined, unit: WindSpeedUnit): string {
  if (kmh == null) return '–';
  return `${Math.round(convertWind(kmh, unit))} ${WIND_LABEL[unit]}`;
}

export function fmtPrecip(mm: number | null | undefined, unit: PrecipitationUnit): string {
  if (mm == null) return '–';
  if (unit === 'inch') return `${(mm / 25.4).toFixed(2)} in`;
  return `${mm < 10 ? mm.toFixed(1) : Math.round(mm)} mm`;
}

export function fmtSnow(cm: number | null | undefined, unit: PrecipitationUnit): string {
  if (cm == null) return '–';
  if (unit === 'inch') return `${(cm / 2.54).toFixed(1)} in`;
  return `${cm < 10 ? cm.toFixed(1) : Math.round(cm)} cm`;
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
export function compass(deg: number | null | undefined): string {
  if (deg == null) return '';
  return COMPASS[Math.round(deg / 45) % 8];
}

export function fmtClock(unix: number | null | undefined): string {
  if (unix == null) return '–';
  return format(new Date(unix * 1000), 'h:mm a').toLowerCase();
}

export function fmtHour(unix: number): string {
  return format(new Date(unix * 1000), 'ha').toLowerCase();
}

export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '–';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `${h} h ${m} min`;
}

const US_AQI_BANDS: [number, string][] = [
  [50, 'Good'],
  [100, 'Moderate'],
  [150, 'Unhealthy for sensitive groups'],
  [200, 'Unhealthy'],
  [300, 'Very unhealthy'],
  [Infinity, 'Hazardous']
];
const EU_AQI_BANDS: [number, string][] = [
  [20, 'Good'],
  [40, 'Fair'],
  [60, 'Moderate'],
  [80, 'Poor'],
  [100, 'Very poor'],
  [Infinity, 'Extremely poor']
];

export function aqiBand(value: number, scale: AqiScale): string {
  const bands = scale === 'us' ? US_AQI_BANDS : EU_AQI_BANDS;
  return bands.find(([max]) => value <= max)![1];
}

export function pickAqi(
  row: { us_aqi?: number | null; european_aqi?: number | null } | null | undefined,
  scale: AqiScale
): number | null {
  if (!row) return null;
  return (scale === 'us' ? row.us_aqi : row.european_aqi) ?? null;
}

// --- Queries ----------------------------------------------------------------

export function useWeatherPrefs() {
  return useQuery({
    queryKey: ['weather-prefs'],
    queryFn: () => api.get<WeatherPrefs>('/api/weather/preferences')
  });
}

export function useWeather(fromKey: string, toKey: string, enabled = true) {
  return useQuery({
    queryKey: ['weather', fromKey, toKey],
    queryFn: () => api.get<WeatherResponse>(`/api/weather?from=${fromKey}&to=${toKey}`),
    enabled,
    // The backend refreshes hourly; this catches a refresh done by another
    // process, whose stream notice only reaches that process's clients.
    refetchInterval: 10 * 60_000,
    refetchOnWindowFocus: true
  });
}

export function groupHoursByDay(hours: HourData[]): Map<string, HourData[]> {
  const out = new Map<string, HourData[]>();
  for (const h of hours) {
    const key = format(new Date(h.t * 1000), 'yyyy-MM-dd');
    const list = out.get(key);
    if (list) list.push(h);
    else out.set(key, [h]);
  }
  return out;
}
